import { Alert, Select, Space, Table, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePlanDisplayIndex } from '@/shared/api/reference'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { shortId } from '@/shared/lib/shortId'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { QueryErrorState } from '@/shared/ui/QueryErrorState'
import { UrlSearchInput } from '@/shared/ui/UrlSearchInput'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { useVoucherHolders } from '@/features/voucher/useVoucherHolders'
import { fetchVoucherTransfers, type VoucherTransfer } from '@/features/voucher/voucherApi'

/** Serverning `listTransfers` chegarasi */
const TRANSFER_LIMIT = 500

/** Partiya o'tkazmalari jurnali (ADR-056 §6): har o'tkazma `VT-yil-NNNNNN` raqami bilan */
export function VoucherTransfersPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const { state, patch } = useTableUrlState()
  const planNames = usePlanDisplayIndex(can('plan.read'))
  const holders = useVoucherHolders()

  const search = state.search.trim()
  const toHolderId = state.filters['holder']?.[0] ?? null

  const transfers = useQuery({
    queryKey: [...queryKeys.vouchers.transfers, { search, toHolderId }],
    queryFn: ({ signal }) =>
      fetchVoucherTransfers({ search, ...(toHolderId ? { toHolderId } : {}), limit: TRANSFER_LIMIT }, signal),
  })
  const rows = transfers.data ?? []

  const dash = t('common.dash')
  const column = (key: string) => t(`voucher.transfers.columns.${key}`)

  const columns: ColumnsType<VoucherTransfer> = [
    {
      key: 'transferNo',
      title: column('transferNo'),
      width: 170,
      fixed: 'left',
      render: (_value, row) => (
        <Typography.Text strong copyable>
          {row.transferNo}
        </Typography.Text>
      ),
    },
    {
      key: 'batchNo',
      title: column('batchNo'),
      width: 100,
      render: (_value, row) => <Link to={`/vouchers?q=${row.batchNo}`}>№{row.batchNo}</Link>,
    },
    {
      key: 'plan',
      title: column('plan'),
      width: 190,
      render: (_value, row) => planNames.get(row.planId) ?? shortId(row.planId),
    },
    {
      key: 'from',
      title: column('from'),
      width: 180,
      render: (_value, row) =>
        row.fromHolderName ?? <Typography.Text type="secondary">{t('voucher.batch.holderOperator')}</Typography.Text>,
    },
    { key: 'to', title: column('to'), width: 180, render: (_value, row) => row.toHolderName ?? dash },
    { key: 'cards', title: column('cards'), width: 100, align: 'right', dataIndex: 'cards' },
    {
      key: 'note',
      title: column('note'),
      width: 200,
      ellipsis: { showTitle: false },
      render: (_value, row) =>
        row.note ? (
          <Tooltip title={row.note}>
            <span dir="auto">{row.note}</span>
          </Tooltip>
        ) : (
          dash
        ),
    },
    { key: 'createdBy', title: column('createdBy'), width: 160, render: (_value, row) => row.createdByName ?? dash },
    {
      key: 'createdAt',
      title: column('createdAt'),
      width: 170,
      render: (_value, row) => (
        <Typography.Text title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</Typography.Text>
      ),
    },
  ]

  return (
    <>
      <PageHeader title={t('nav.voucherTransfers')} description={t('pageIntro.voucherTransfers')} />

      <Space wrap style={{ marginBottom: 16 }}>
        <UrlSearchInput placeholder={t('voucher.transfers.searchPlaceholder')} width={260} />
        {can('org.node.read') ? (
          <Select<string | null>
            allowClear
            showSearch
            optionFilterProp="label"
            loading={holders.loading}
            placeholder={t('voucher.transfers.holderPlaceholder')}
            value={toHolderId}
            onChange={(value) => patch({ page: 1, filters: { ...state.filters, holder: value ? [value] : [] } })}
            style={{ minWidth: 240 }}
            options={holders.selectable.map((node) => ({ value: node.id, label: `${node.displayName} (${node.code})` }))}
          />
        ) : null}
      </Space>

      {rows.length >= TRANSFER_LIMIT ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('voucher.transfers.limited', { limit: TRANSFER_LIMIT })}
        />
      ) : null}

      {transfers.isError ? (
        <QueryErrorState error={transfers.error} onRetry={() => void transfers.refetch()} />
      ) : (
        <Table<VoucherTransfer>
          dataSource={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={transfers.isPending}
          size="small"
          pagination={false}
          scroll={{ x: 1450 }}
          locale={{
            emptyText: (
              <EmptyState
                title={t('voucher.transfers.empty')}
                description={t(search || toHolderId ? 'voucher.transfers.emptyFilteredHint' : 'voucher.transfers.emptyHint')}
              />
            ),
          }}
        />
      )}
    </>
  )
}
